import React, { useState, useEffect, useRef } from 'react';
import './GroupMultiSelect.css';
import SimpleCheckbox from '../SimpleCheckbox/SimpleCheckbox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import logComponent from '../../common/logComponent';

export default function GroupMultiSelect({ selectedGroupIds, allGroups, label, name, validate: propsValidate, onValidate, groupsError, selectedGroupIdsError, groupsLoading, selectedGroupIdsLoading, reloadGroups: propsReloadGroups, reloadSelectedGroupIds }) {

  const [groups, setGroups] = useState(allGroups);
  const [expanded, setExpanded] = useState(false);
  const [userSelectedGroupIds, setUserSelectedGroupIds] = useState(selectedGroupIds ? selectedGroupIds : []);
  const [userSelectedGroups, setUserSelectedGroups] = useState(null);
  const userSelectedGroupIdsRef = useRef(selectedGroupIds ? selectedGroupIds : []);
  const setUserSelectedGroupIdsRef = (el) => {
    if (logComponent('GroupMultiSelect')) { console.debug('GroupMultiSelect setUserSelectedGroupIdsRef'); }
    userSelectedGroupIdsRef.current = el;
    setUserSelectedGroupIds(el);
  }

  const userSelectedGroupsRef = useRef(null);
  const setUserSelectedGroupsRef = (el) => {
    if (logComponent('GroupMultiSelect')) { console.debug('GroupMultiSelect setUserSelectedGroupsRef'); }
    userSelectedGroupsRef.current = el;
    setUserSelectedGroups(el);
  }


  const expandedRef = useRef(null);
  const setExpandedRef = (el) => {
    if (logComponent('GroupMultiSelect')) { console.debug('GroupMultiSelect setExpandedRef'); }
    expandedRef.current = el;
    setExpanded(el);
  }

  const [needsValidate, setNeedsValidate] = useState(false);

  if (logComponent('GroupMultiSelect')) { console.debug('GroupMultiSelect render'); }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (logComponent('GroupMultiSelect')) { console.debug('GroupMultiSelect onMount'); }
    submitSelectedGroups();
  }, []);

  useEffect(() => {
    if (logComponent('GroupMultiSelect')) { console.debug('GroupMultiSelect useEffect [allGroups]'); }
    setGroups(allGroups);
  }, [allGroups]);

  useEffect(() => {
    if (logComponent('GroupMultiSelect')) { console.debug('GroupMultiSelect useEffect [selectedGroupIds]'); }
    setUserSelectedGroupIdsRef(selectedGroupIds ? selectedGroupIds : []);
  }, [selectedGroupIds]);

  useEffect(() => {
    if (logComponent('GroupMultiSelect')) { console.debug('GroupMultiSelect useEffect [userSelectedGroupIds]'); }
    submitSelectedGroups();
  }, [userSelectedGroupIds]);

  useEffect(() => {
    if (logComponent('GroupMultiSelect')) { console.debug('GroupMultiSelect useEffect [needsValidate]'); }
    if (needsValidate === true) validate();
    setNeedsValidate(false);
  }, [needsValidate]);

  const validate = () => {
    if (logComponent('GroupMultiSelect')) { console.debug('GroupMultiSelect validate'); }
    //TODO:  What to put here.  Method was missing
  }
  const submitSelectedGroups = () => {
    if (logComponent('GroupMultiSelect')) { console.debug('GroupMultiSelect submitSelectedGroups'); }

    let selectedGroups;

    if (!groups || !userSelectedGroupIdsRef.current) {
      selectedGroups = null;
    } else {
      // This .filter() below removes selected groups that are no longer among the potential selections
      // This comes up in the UserUpdateModal when the user's facility/corporation/statussolutions has been changed; the
      // selected groups (from the old facility/corporation/status solutions) no longer match any of the potential groups
      // (from the new facility/corporation/status solutions), if they are not filtered, the .map returns undefined
      // for each of those items
      selectedGroups = userSelectedGroupIdsRef.current
        .filter(selectedGroupId => groups.some(group => group.groupId === selectedGroupId))
        .map(selectedGroupId => groups.find(group => group.groupId === selectedGroupId))
    }

    onValidate({
      name: name,
      isValid: true,
      value: selectedGroups
    });
  }

  const logGroupSelectedState = (group) => {
    if (logComponent('GroupMultiSelect')) { console.debug('GroupMultiSelect logGroupSelectedState'); }
    if (!userSelectedGroupsRef.current) return;
    const newUserSelectedGroups = [...userSelectedGroupsRef.current, group];
    setUserSelectedGroupsRef(newUserSelectedGroups)
    setNeedsValidate(true);
  }

  const toggleExpanded = () => {
    if (logComponent('GroupMultiSelect')) { console.debug('GroupMultiSelect toggleExpanded'); }
   setExpandedRef(!expandedRef.current);
  }

  const toggleGroupSelection = (item) => {
    if (logComponent('GroupMultiSelect')) { console.debug('GroupMultiSelect toggleGroupSelection'); }

    if (item.selected) {
      if (!userSelectedGroupIdsRef.current.some(id => id === item.name)) {
        setUserSelectedGroupIdsRef(userSelectedGroupIdsRef.current.concat(item.name));
      }
    } else if (!item.selected) {
      setUserSelectedGroupIdsRef(userSelectedGroupIdsRef.current.filter(id => id !== item.name));
    }
  }

  const reloadGroups = () => {
    if (logComponent('GroupMultiSelect')) { console.debug('GroupMultiSelect reloadGroups'); }

    if (groupsError) {
      propsReloadGroups();
    }
    if (selectedGroupIdsError) {
      reloadSelectedGroupIds();
    }
  }

  return (
    <div className="Group-Multi-Select">
      <label
        className="Group-Multi-Select__label"
        htmlFor={name + '-input'}>
        {label}
      </label>
      <div className="Group-Multi-Select__body">
        <div
          className="Group-Multi-Select__heading"
          onClick={toggleExpanded}>
          <span className="Group-Multi-Select__heading-text">Select groups</span>
          <button type="button" className="Group-Multi-Select__expand-btn">
            <FontAwesomeIcon className={'Group-Multi-Select__expand-btn-icon' + (expanded ? ' Group-Multi-Select__heading-icon--minus' : '')} icon={expanded ? 'minus-circle' : 'plus-circle'} />
          </button>
        </div>
        <div className={'Group-Multi-Select__panel' + (expanded ? ' Group-Multi-Select__panel--expanded' : '')}>
          {
            !!groups && !groupsLoading && !groupsError && !selectedGroupIdsLoading && !selectedGroupIdsError &&
            <ul className="Group-Multi-Select__groups">
              {
                  groups.length > 0 ?
                  groups.map(group =>
                    <li
                      className="Group-Multi-Select__group"
                      key={group.groupId}>
                      <SimpleCheckbox
                        label={group.name}
                        name={group.groupId}
                        selected={userSelectedGroupIds && userSelectedGroupIds.some(id => group.groupId === id)}
                        toggleSelection={toggleGroupSelection}
                        validate={propsValidate}
                        onValidate={logGroupSelectedState} />
                    </li>) :
                  <li key="noGroups" className="Group-Multi-Select__empty">There are no groups connected with this organization.</li>
              }
            </ul>
          }
          {
            (groupsLoading || selectedGroupIdsLoading) &&
            <p className="Group-Multi-Select__loading">Loading groups...</p>
          }
          {
            (groupsError || selectedGroupIdsError) &&
            <p className="Group-Multi-Select__error">
              Error loading groups.
              <button
                type="button"
                className="Group-Multi-Select__retry"
                onClick={reloadGroups}>
                Try again
              </button>
            </p>
          }
        </div>
      </div>
    </div>
  );
}